/**
 * Pure helpers for the shared AddJobCategoryWizard (ER-11356, Organization > Job
 * Categories). The wizard scores a new Job Category with the EIGE / European
 * Commission Gender-Neutral Job Evaluation Toolkit "4-Factor" method: Skills,
 * Responsibility, Effort and Working Conditions. Each factor is broken into
 * subfactors; Knowledge is scored 0–8 (aligned to the European Qualifications
 * Framework) and every other subfactor is scored 0–5. Level 0 means
 * "does not apply" and is a valid, selectable score.
 *
 * Level descriptions under each slider are the official level descriptors from
 * the EIGE "EU-wide guidelines on gender-neutral job evaluation and
 * classification: Step-by-step toolkit" (26 March 2026), as written out in
 * EIGE Toolkit §4 (EC space), verbatim — including level 0 for every subfactor
 * and levels 0–8 for Knowledge.
 *
 * Kept framework-free so unit tests can exercise the step/score model, per-step
 * validation, factor totals and the submit payload without mounting the SFC
 * (mirrors the approach used by JobCategoriesList/jobCategoriesListRow.js).
 */

/** Default subfactor maximum — the fourteen non-Knowledge subfactors top out at 5. */
export const MAX_LEVEL = 5;

/** Knowledge uses levels 0–8, aligned to the European Qualifications Framework. */
export const KNOWLEDGE_MAX_LEVEL = 8;

export const STEP = {
  INFO: "Info",
  SKILLS: "Skills",
  RESPONSIBILITY: "Responsibility",
  EFFORT: "Effort",
  WORKING_CONDITIONS: "Working Conditions",
  REVIEW: "Review",
};

export const STEPS = [
  { key: STEP.INFO, label: "Info" },
  { key: STEP.SKILLS, label: "Skills" },
  { key: STEP.RESPONSIBILITY, label: "Responsibility" },
  { key: STEP.EFFORT, label: "Effort" },
  { key: STEP.WORKING_CONDITIONS, label: "Working Conditions" },
  { key: STEP.REVIEW, label: "Review" },
];

export const WIZARD_TITLE = "Add Job Category";

export const CREATE_BUTTON_LABEL = "Create job category";

export const NEXT_BUTTON_LABEL = "Next";

/**
 * The maximum level of a subfactor — its own scale, not a global value.
 * Knowledge carries `maxLevel: 8`; every other subfactor carries `maxLevel: 5`.
 * A subfactor without an explicit `maxLevel` falls back to `MAX_LEVEL`.
 */
export function subfactorMaxLevel(subfactor) {
  if (!subfactor || typeof subfactor !== "object") return MAX_LEVEL;
  const max = Number(subfactor.maxLevel);
  return Number.isInteger(max) && max >= 0 ? max : MAX_LEVEL;
}

/** The selectable levels for a subfactor: `[0, 1, …, max]`. */
export function subfactorScoreLevels(subfactor) {
  const levels = [];
  for (let level = 0; level <= subfactorMaxLevel(subfactor); level += 1) {
    levels.push(level);
  }
  return levels;
}

/**
 * The four scored factors. `stepKey` links a factor to the wizard step that scores
 * it; `summaryLetter` matches the JobCategoriesList row model (`factorScores` keyed
 * s/e/r/wc as 0–100 percentages) so a created category can render straight in the
 * list without conversion.
 *
 * Each subfactor carries `maxLevel` (8 for Knowledge, 5 elsewhere) and
 * `levelDescriptions`: the official EIGE level descriptors (0–max) shown beneath
 * the slider once a level is selected.
 */
export const FACTORS = [
  {
    key: "skills",
    stepKey: STEP.SKILLS,
    label: "Skills",
    summaryLetter: "s",
    subfactors: [
      {
        key: "knowledge",
        label: "Knowledge",
        maxLevel: KNOWLEDGE_MAX_LEVEL,
        description: "The depth and breadth of knowledge needed to carry out the role.",
        levelDescriptions: {
          0: "Does not apply. No specific knowledge beyond basic orientation.",
          1: "Basic general knowledge; the job requires basic knowledge to carry out simple tasks.",
          2: "Basic factual knowledge of a field of work; simple thinking and practical knowledge to use information, complete tasks and solve routine problems using basic rules and tools.",
          3: "Knowledge of facts, principles and general ideas in a job area; a range of thinking and practical knowledge to do tasks and solve problems by choosing and using basic methods, tools, materials and information.",
          4: "Factual and theoretical knowledge in broad contexts within a field of work; a range of thinking and practical knowledge to find solutions to specific problems.",
          5: "Specialised and theoretical knowledge within a field of work and an awareness of the limits of that knowledge; comprehensive cognitive and practical knowledge to develop creative solutions to abstract problems.",
          6: "Advanced knowledge of a field of work with a deep understanding of theories and principles; high-level expertise to solve complex and unexpected problems within a specialised area.",
          7: "Very specialised knowledge including the newest ideas in the field; deep awareness of knowledge issues within and between fields; specialised skills for research and innovation.",
          8: "Knowledge at the highest, most advanced level in a field and where fields overlap; combining and judging ideas to solve critical research and innovation problems and expand or change existing knowledge or professional practice.",
        },
      },
      {
        key: "interpersonalCommunication",
        label: "Interpersonal and communication skills",
        maxLevel: MAX_LEVEL,
        description: "The communication and people skills the role requires.",
        levelDescriptions: {
          0: "Not required.",
          1: "Basic, routine communication with a familiar audience.",
          2: "Explains information, listens and adjusts communication in routine interactions.",
          3: "Persuades, negotiates or communicates complex information to varied stakeholders.",
          4: "Handles sensitive, conflicting or high-stakes communication; builds alignment.",
          5: "Influences at the highest level across diverse, conflicting or specialised groups.",
        },
      },
      {
        key: "problemSolving",
        label: "Problem-solving skills",
        maxLevel: MAX_LEVEL,
        description: "The judgement and problem-solving the work involves.",
        levelDescriptions: {
          0: "Not required.",
          1: "Follows a standard solution for simple issues.",
          2: "Identifies causes and selects from known solutions.",
          3: "Analyses non-routine problems and develops workable solutions.",
          4: "Solves complex or ambiguous problems using judgement and original approaches.",
          5: "Creates solutions for novel, systemic or critical problems with far-reaching consequences.",
        },
      },
      {
        key: "planningOrganisational",
        label: "Planning and organisational skills",
        maxLevel: MAX_LEVEL,
        description: "The planning and organisational demands of the role.",
        levelDescriptions: {
          0: "Not required.",
          1: "Organises own routine tasks using clear instructions.",
          2: "Plans own work and coordinates routine priorities.",
          3: "Plans interdependent work, resources and deadlines.",
          4: "Coordinates multiple complex priorities with significant dependencies.",
          5: "Designs and integrates complex plans across functions or long time horizons.",
        },
      },
      {
        key: "physicalSkills",
        label: "Physical skills",
        maxLevel: MAX_LEVEL,
        description: "The physical skills and dexterity the role requires.",
        levelDescriptions: {
          0: "Not required.",
          1: "Basic coordinated movement or manual handling.",
          2: "Regular practical or fine-motor skill with normal accuracy.",
          3: "High coordination, precision or sustained manual skill.",
          4: "Advanced precision, dexterity, strength, speed or technical bodily control.",
          5: "Exceptional or highly specialised physical skill, control or stamina required by the job.",
        },
      },
    ],
  },
  {
    key: "responsibility",
    stepKey: STEP.RESPONSIBILITY,
    label: "Responsibility",
    summaryLetter: "r",
    subfactors: [
      {
        key: "people",
        label: "People",
        maxLevel: MAX_LEVEL,
        description: "Accountability for leading, managing or supporting people.",
        levelDescriptions: {
          0: "No responsibility for others.",
          1: "Supports colleagues or service users under clear direction.",
          2: "Provides regular guidance, care, service or day-to-day coordination.",
          3: "Supervises, develops or safeguards the wellbeing of a team or a significant group.",
          4: "Has substantial responsibility for people's safety, development, outcomes or wellbeing.",
          5: "Responsibility for the wellbeing, safety or outcomes of a very large, vulnerable or critical population.",
        },
      },
      {
        key: "goodsEquipment",
        label: "Goods and equipment",
        maxLevel: MAX_LEVEL,
        description: "Responsibility for goods, equipment, materials or premises.",
        levelDescriptions: {
          0: "No meaningful responsibility.",
          1: "Uses or safeguards routine tools and equipment.",
          2: "Maintains equipment, stock or materials with limited autonomy.",
          3: "Responsible for valuable, specialised or operationally critical assets.",
          4: "Accountable for major, complex or high-risk assets and continuity.",
          5: "Accountable for extensive, irreplaceable or organisation-critical resources.",
        },
      },
      {
        key: "information",
        label: "Information",
        maxLevel: MAX_LEVEL,
        description: "Responsibility for handling, protecting and using information.",
        levelDescriptions: {
          0: "No access beyond ordinary personal information.",
          1: "Records, retrieves or handles routine information.",
          2: "Maintains accurate records and handles moderately sensitive information.",
          3: "Controls confidential, regulated or decision-critical information.",
          4: "Owns highly sensitive information, systems, privacy or security controls.",
          5: "Sets information governance or protects information with exceptional strategic or legal impact.",
        },
      },
      {
        key: "financialResources",
        label: "Financial resources",
        maxLevel: MAX_LEVEL,
        description: "Responsibility for budgets, funds or financial resources.",
        levelDescriptions: {
          0: "No financial responsibility.",
          1: "Handles small transactions or follows basic controls.",
          2: "Processes or monitors defined budgets, payments or purchasing.",
          3: "Manages a material budget, approvals or financial risk within policy.",
          4: "Has significant delegated authority over budgets, revenue, costs or financial exposure.",
          5: "Accountable for very substantial resources or decisions with major financial consequences.",
        },
      },
    ],
  },
  {
    key: "effort",
    stepKey: STEP.EFFORT,
    label: "Effort",
    summaryLetter: "e",
    subfactors: [
      {
        key: "mental",
        label: "Mental effort",
        maxLevel: MAX_LEVEL,
        description: "The mental concentration and analysis the work demands.",
        levelDescriptions: {
          0: "None beyond ordinary attention.",
          1: "Basic attention and simple instructions.",
          2: "Regular focus, detail and straightforward analysis.",
          3: "Frequent concentration on complex tasks or data.",
          4: "Continuous high-level concentration with few breaks.",
          5: "Sustained, intense cognitive effort over long periods involving creative or novel problems.",
        },
      },
      {
        key: "psychosocialEmotional",
        label: "Psychosocial and emotional effort",
        maxLevel: MAX_LEVEL,
        description: "The emotional and psychosocial demands of the work.",
        levelDescriptions: {
          0: "Not required.",
          1: "Routine, low-intensity interaction with limited emotional demand.",
          2: "Regular empathy, patience or emotional regulation in ordinary difficult interactions.",
          3: "Frequent exposure to distress, conflict or emotionally demanding situations.",
          4: "Very high emotional regulation and resilience; repeated exposure to serious or traumatic situations.",
          5: "Intense and sustained psychosocial or emotional demand with serious consequences if not managed.",
        },
      },
      {
        key: "physicalEffort",
        label: "Physical effort",
        maxLevel: MAX_LEVEL,
        description: "The physical effort required to carry out the work.",
        levelDescriptions: {
          0: "Not required.",
          1: "Light, occasional physical activity.",
          2: "Moderate activity, handling, standing or movement.",
          3: "Regular demanding activity, awkward postures, lifting or sustained movement.",
          4: "High or sustained exertion, difficult conditions or substantial physical strain.",
          5: "Intense, prolonged or exceptional physical exertion, force, stamina or recovery demand.",
        },
      },
    ],
  },
  {
    key: "workingConditions",
    stepKey: STEP.WORKING_CONDITIONS,
    label: "Working Conditions",
    summaryLetter: "wc",
    subfactors: [
      {
        key: "environment",
        label: "Environment (physical, psychological or emotional)",
        maxLevel: MAX_LEVEL,
        description: "The physical, psychological or emotional conditions of the workplace.",
        levelDescriptions: {
          0: "Ordinary, low-risk working environment.",
          1: "Minor discomforts or low-level exposure that is infrequent and controlled.",
          2: "Regular exposure to moderate noise, discomfort, stress, weather, hazards or emotionally difficult conditions.",
          3: "Frequent or significant physical, psychological or emotional exposure requiring controls and recovery.",
          4: "Very demanding, hazardous, distressing or unpredictable environment despite controls.",
          5: "Extreme or sustained exposure to serious hazards, extreme conditions or highly distressing circumstances.",
        },
      },
      {
        key: "organisationalEnvironment",
        label: "Organisational environment",
        maxLevel: MAX_LEVEL,
        description:
          "The organisational context, pressures and flexibility the role is exposed to.",
        levelDescriptions: {
          0: "Predictable hours and location; no unusual organisational constraints.",
          1: "Occasional schedule, location or connectivity variation.",
          2: "Some irregular hours, shift variation, travel, on-call work or digital availability.",
          3: "Regular shifts, unsocial hours, substantial travel or persistent connectivity demands.",
          4: "Highly irregular or extended hours, extensive travel, frequent disruption or near-continuous availability.",
          5: "Exceptional organisational constraints with major, sustained impact on working time, location or personal life.",
        },
      },
    ],
  },
];

export function stepIndex(stepKey) {
  const index = STEPS.findIndex((step) => step.key === stepKey);
  return index === -1 ? 1 : index + 1;
}

export function factorForStep(stepKey) {
  return FACTORS.find((factor) => factor.stepKey === stepKey) || null;
}

export function factorByKey(factorKey) {
  return FACTORS.find((factor) => factor.key === factorKey) || null;
}

/** The factor's maximum: the sum of each subfactor's own maximum level. */
export function maxFactorScore(factor) {
  if (!factor) return 0;
  return factor.subfactors.reduce((sum, subfactor) => sum + subfactorMaxLevel(subfactor), 0);
}

/**
 * True when `value` is an integer level within a subfactor's own scale
 * (0..max). A deliberate 0 is a valid score — it is NOT treated as "not
 * scored"; only `null` / `undefined` / `""` (and out-of-range values) are.
 */
export function isScoreSet(value, maxLevel) {
  if (value === null || value === undefined || value === "") return false;
  const numeric = Number(value);
  const max = Number.isInteger(maxLevel) ? maxLevel : MAX_LEVEL;
  return Number.isInteger(numeric) && numeric >= 0 && numeric <= max;
}

/**
 * The official EIGE level descriptor shown beneath a subfactor's slider for a
 * given level (including level 0). Returns "" when the subfactor has no
 * descriptor for that level.
 */
export function subfactorLevelDescription(subfactor, level) {
  if (!subfactor || !isScoreSet(level, subfactorMaxLevel(subfactor))) return "";
  const value = Number(level);
  const descriptions = subfactor.levelDescriptions;
  if (!descriptions || typeof descriptions[value] !== "string") return "";
  return descriptions[value];
}

/** Hover / focus tooltip title for a level circle: "Level 5", or "Level 0 — Does not apply". */
export function levelTooltipTitle(level) {
  return Number(level) === 0 ? "Level 0 — Does not apply" : `Level ${Number(level)}`;
}

/**
 * One `{ level, title, description }` entry per selectable level of a subfactor, for the
 * level-circle tooltips. The description is the same official descriptor that feeds the
 * selected-level text beneath the slider.
 */
export function subfactorLevelTooltips(subfactor) {
  return subfactorScoreLevels(subfactor).map((level) => ({
    level,
    title: levelTooltipTitle(level),
    description: subfactorLevelDescription(subfactor, level),
  }));
}

export function nameIsComplete(name) {
  return typeof name === "string" && name.trim().length > 0;
}

/**
 * Normalise an arbitrary scores object into the full nested shape
 * `{ skills: { knowledge: <0–8|null>, ... }, ... }`, filling any missing
 * subfactor with `null` (not yet scored). A deliberate 0 is preserved as a
 * valid score. Unknown keys are dropped.
 */
export function normalizeScores(rawScores) {
  const source = rawScores && typeof rawScores === "object" ? rawScores : {};
  const normalized = {};
  FACTORS.forEach((factor) => {
    const factorScores =
      source[factor.key] && typeof source[factor.key] === "object" ? source[factor.key] : {};
    normalized[factor.key] = {};
    factor.subfactors.forEach((subfactor) => {
      const value = factorScores[subfactor.key];
      normalized[factor.key][subfactor.key] = isScoreSet(value, subfactorMaxLevel(subfactor))
        ? Number(value)
        : null;
    });
  });
  return normalized;
}

/** The scores for a single factor, `{ [subfactorKey]: <0–max|null> }`. */
export function factorScoresOf(normalizedScores, factor) {
  const out = {};
  if (!factor || !normalizedScores || !normalizedScores[factor.key]) return out;
  factor.subfactors.forEach((subfactor) => {
    const value = normalizedScores[factor.key][subfactor.key];
    out[subfactor.key] = isScoreSet(value, subfactorMaxLevel(subfactor)) ? Number(value) : null;
  });
  return out;
}

/** True when every subfactor of the factor has been scored (0 counts as scored). */
export function factorIsComplete(normalizedScores, factor) {
  if (!factor || !normalizedScores || !normalizedScores[factor.key]) return false;
  return factor.subfactors.every((subfactor) =>
    isScoreSet(normalizedScores[factor.key][subfactor.key], subfactorMaxLevel(subfactor))
  );
}

/** Sum of the scored subfactor levels (unscored subfactors contribute 0). */
export function factorTotal(normalizedScores, factor) {
  if (!factor || !normalizedScores || !normalizedScores[factor.key]) return 0;
  return factor.subfactors.reduce((sum, subfactor) => {
    const value = Number(normalizedScores[factor.key][subfactor.key]);
    return isScoreSet(value, subfactorMaxLevel(subfactor)) ? sum + value : sum;
  }, 0);
}

export function scorePercent(total, max) {
  if (!Number.isFinite(total) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((total / max) * 100)));
}

/**
 * The 4-Factor Score summary in the JobCategoriesList row shape
 * (`{ s, e, r, wc }`, 0–100 percentages). A factor that is not fully scored
 * reports `null` for its letter, matching the list's "no factor scores" state.
 */
export function buildFactorScoreSummary(normalizedScores) {
  const summary = {};
  FACTORS.forEach((factor) => {
    const complete = factorIsComplete(normalizedScores, factor);
    summary[factor.summaryLetter] = complete
      ? scorePercent(factorTotal(normalizedScores, factor), maxFactorScore(factor))
      : null;
  });
  return summary;
}

/** One Review row: `{ key, label, max, complete, total, display }`. */
export function reviewRowView(normalizedScores, factor) {
  const max = maxFactorScore(factor);
  const complete = factorIsComplete(normalizedScores, factor);
  const total = factorTotal(normalizedScores, factor);
  return {
    key: factor.key,
    label: factor.label,
    max,
    complete,
    total,
    display: complete ? `${total} / ${max}` : `– / ${max}`,
  };
}

/** Whether the required inputs for a single step have been filled. */
export function stepIsComplete(stepKey, name, normalizedScores) {
  if (stepKey === STEP.INFO) return nameIsComplete(name);
  const factor = factorForStep(stepKey);
  if (factor) return factorIsComplete(normalizedScores, factor);
  // Review is "complete" only once the whole wizard is ready to submit.
  return nameIsComplete(name) && FACTORS.every((f) => factorIsComplete(normalizedScores, f));
}

/** Whether every required input across the whole wizard is filled. */
export function wizardIsComplete(name, normalizedScores) {
  if (!nameIsComplete(name)) return false;
  return FACTORS.every((factor) => factorIsComplete(normalizedScores, factor));
}

/**
 * The current step's field values, emitted with the `next` event:
 * Info -> `{ name }`; a factor step -> `{ [factorKey]: { subfactorKey: level } }`.
 */
export function stepFieldValues(stepKey, name, normalizedScores) {
  if (stepKey === STEP.INFO) {
    return { name: typeof name === "string" ? name : "" };
  }
  const factor = factorForStep(stepKey);
  return factor ? { [factor.key]: factorScoresOf(normalizedScores, factor) } : {};
}

/**
 * Full payload emitted with the `submit` event ("Create job category"): the name,
 * every subfactor score (0–8 for Knowledge, 0–5 elsewhere) grouped by factor, and
 * the aggregated 4-Factor Score summary in the JobCategoriesList row shape
 * (`factorScores.s/e/r/wc`, 0–100).
 */
export function buildSubmitPayload(name, normalizedScores) {
  const scores = {};
  FACTORS.forEach((factor) => {
    scores[factor.key] = factorScoresOf(normalizedScores, factor);
  });
  return {
    name: typeof name === "string" ? name.trim() : "",
    scores,
    factorScores: buildFactorScoreSummary(normalizedScores),
  };
}
